/**
 * RuleSliders
 *
 * 2 sliders fundamentales que rigen el comportamiento de la IA:
 *  - Voz y Tono (Formal ↔ Amigable)
 *  - Balance de Estrategia (Proteger BD ↔ Max Ingresos)
 *
 * Cada card tiene un footer "Aplicada en:" que muestra dónde impacta esta
 * regla en el resto del producto — para que el usuario entienda que estos
 * sliders NO son cosméticos.
 */

import { Type, Scale, FileText, MessageCircle } from "lucide-react";
import { Card } from "../../../shared/components/ui/Card";

interface RuleSlidersProps {
    tone: number;
    setTone: (val: number) => void;
    aggressiveness: number;
    setAggressiveness: (val: number) => void;
}

function tonePreview(tone: number): string {
    if (tone < 30) return "Te recomendamos revisar tu pedido.";
    if (tone > 70) return "¡Eh! Mirá estas ofertas increíbles 😎";
    return "Notamos que aún no hiciste un pedido.";
}

function aggressivenessDescription(a: number): string {
    if (a < 40)
        return "Prioriza relaciones a largo plazo. Menos mensajes, ofertas chicas.";
    if (a > 70)
        return "Prioriza ventas inmediatas. Mayor frecuencia y urgencia.";
    return "Balance entre ventas inmediatas y retención.";
}

export default function RuleSliders({
    tone,
    setTone,
    aggressiveness,
    setAggressiveness,
}: RuleSlidersProps) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Voz y Tono */}
            <Card className="p-5 flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-pink-50 rounded-lg text-pink-500">
                        <Type size={18} />
                    </div>
                    <div>
                        <h3 className="text-slate-900 font-bold text-base">
                            Voz y Tono
                        </h3>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wide font-bold">
                            Personalidad
                        </p>
                    </div>
                </div>

                <div className="space-y-5 flex-1">
                    <div>
                        <div className="flex justify-between text-xs text-slate-600 mb-2 font-medium">
                            <span>Formal</span>
                            <span className="text-slate-900 font-bold">
                                {tone}
                            </span>
                            <span>Amigable</span>
                        </div>
                        <input
                            type="range"
                            min="0"
                            max="100"
                            value={tone}
                            onChange={(e) => setTone(parseInt(e.target.value))}
                            aria-label="Voz y tono (de Formal a Amigable)"
                            aria-valuetext={`${tone}: ${tone < 30 ? "Formal" : tone > 70 ? "Amigable" : "Neutral"}`}
                            className="w-full h-1.5 bg-gradient-to-r from-blue-200 to-pink-200 rounded-lg appearance-none cursor-pointer accent-pink-500"
                        />
                    </div>
                    <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                        <p className="text-slate-700 text-xs italic line-clamp-2">
                            "{tonePreview(tone)}"
                        </p>
                    </div>
                </div>

                <AppliedInFooter
                    items={[
                        { icon: <FileText size={11} />, label: "Plantillas WhatsApp generadas por IA" },
                        { icon: <MessageCircle size={11} />, label: "Push notifications de campañas" },
                    ]}
                />
            </Card>

            {/* Balance de Estrategia */}
            <Card className="p-5 flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-yellow-50 rounded-lg text-yellow-600">
                        <Scale size={18} />
                    </div>
                    <div>
                        <h3 className="text-slate-900 font-bold text-base">
                            Balance de Estrategia
                        </h3>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wide font-bold">
                            Agresividad
                        </p>
                    </div>
                </div>

                <div className="space-y-5 flex-1">
                    <div>
                        <div className="flex justify-between text-xs text-slate-600 mb-2 font-medium">
                            <span>Proteger BD</span>
                            <span className="text-slate-900 font-bold">
                                {aggressiveness}
                            </span>
                            <span>Max. Ingresos</span>
                        </div>
                        <input
                            type="range"
                            min="0"
                            max="100"
                            value={aggressiveness}
                            onChange={(e) =>
                                setAggressiveness(parseInt(e.target.value))
                            }
                            aria-label="Balance de estrategia (de Proteger base a Maximizar ingresos)"
                            aria-valuetext={`${aggressiveness}: ${aggressiveness < 40 ? "Conservadora" : aggressiveness > 70 ? "Agresiva" : "Balanceada"}`}
                            className="w-full h-1.5 bg-gradient-to-r from-green-200 to-red-200 rounded-lg appearance-none cursor-pointer accent-yellow-500"
                        />
                    </div>
                    <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                        <p className="text-slate-700 text-xs italic">
                            {aggressivenessDescription(aggressiveness)}
                        </p>
                    </div>
                </div>

                <AppliedInFooter
                    items={[
                        { icon: <FileText size={11} />, label: "Tamaño de descuentos sugeridos por IA" },
                        { icon: <MessageCircle size={11} />, label: "Cadencia de campañas automáticas" },
                    ]}
                />
            </Card>
        </div>
    );
}

/* ─── Footer compartido ─── */
export function AppliedInFooter({
    items,
}: {
    items: { icon: React.ReactNode; label: string }[];
}) {
    return (
        <div className="mt-4 pt-3 border-t border-slate-100">
            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
                Aplicada en
            </p>
            <ul className="space-y-1">
                {items.map((it, i) => (
                    <li
                        key={i}
                        className="flex items-center gap-1.5 text-[11px] text-slate-600"
                    >
                        <span className="text-violet-500">{it.icon}</span>
                        {it.label}
                    </li>
                ))}
            </ul>
        </div>
    );
}
